import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import agendamentosService from '../../services/agendamentosService';
import funcionariosService from '../../services/funcionariosService';
import clientesService from '../../services/clientesService';
import servicosService from '../../services/servicosService';
import { usePermissao } from '../../hooks/usePermissao';

// 🔥 FUNÇÃO CORRIGIDA - NÃO SOBRESCREVE A COMISSÃO DO SERVIÇO
const calcularComissaoServico = (servico, profissional) => {
  // 🔥 PRIORIDADE 1: Usar a comissão do serviço
  if (servico.comissao_percentual && servico.comissao_percentual > 0) {
    console.log(`💰 Usando comissão do serviço "${servico.nome}": ${servico.comissao_percentual}%`);
    return servico.comissao_percentual;
  }
  
  // 🔥 PRIORIDADE 2: Usar a comissão do profissional
  if (profissional && profissional.comissao_percentual && profissional.comissao_percentual > 0) {
    console.log(`💰 Usando comissão do profissional "${profissional.nome}": ${profissional.comissao_percentual}%`);
    return profissional.comissao_percentual;
  }
  
  // 🔥 FALLBACK: 30%
  console.log(`💰 Usando comissão padrão: 30%`);
  return 30;
};

// 🔥 FUNÇÃO PARA EXTRAIR DATA CORRETAMENTE (SEM CONVERSÃO DE TIMEZONE)
const extrairDataCorreta = (dataHoraStr) => {
  if (!dataHoraStr) return format(new Date(), 'yyyy-MM-dd');
  
  let match = dataHoraStr.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) {
    return match[1];
  }
  
  match = dataHoraStr.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) {
    return match[1];
  }
  
  return format(new Date(), 'yyyy-MM-dd');
};

// 🔥 FUNÇÃO PARA EXTRAIR HORA - CONVERTER UTC PARA LOCAL
const extrairHoraCorreta = (dataHoraStr) => {
  if (!dataHoraStr) return '09:00';
  
  let match = dataHoraStr.match(/T(\d{2}):(\d{2})/);
  if (match) {
    let horaUTC = parseInt(match[1]);
    let minuto = parseInt(match[2]);
    
    let horaLocal = horaUTC - 3;
    if (horaLocal < 0) {
      horaLocal = horaLocal + 24;
    }
    
    console.log('🔄 Conversão UTC -> Local:', `${horaUTC}:${minuto} -> ${horaLocal}:${minuto}`);
    
    return `${horaLocal.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}`;
  }
  
  match = dataHoraStr.match(/\s(\d{2}):(\d{2})/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }
  
  return '09:00';
};

export const useAgendamentos = () => {
  const { podeVer, podeEditar, podeExcluir, nivel, usuario } = usePermissao();
  const isFuncionario = nivel === 'funcionario';
  
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState(null);
  const [filtroProfissional, setFiltroProfissional] = useState('todos');
  const [filtroServico, setFiltroServico] = useState('todos');
  const [agendamentos, setAgendamentos] = useState([]);
  const [agendamentosFiltrados, setAgendamentosFiltrados] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionariosFiltrados, setFuncionariosFiltrados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horarioInicial, setHorarioInicial] = useState(null);
  
  const [formData, setFormData] = useState({
    clienteId: '',
    profissionalId: '',
    servicoId: '',
    data: '',
    hora: '09:00',
    status: 'agendado',
    observacoes: ''
  });

  // 🔥 FUNÇÃO PARA RESETAR FORMULÁRIO
  const resetarFormData = useCallback(() => {
    console.log('🔄 Resetando formulário');
    setFormData({
      clienteId: '',
      profissionalId: '',
      servicoId: '',
      data: format(dataSelecionada, 'yyyy-MM-dd'),
      hora: '09:00',
      status: 'agendado',
      observacoes: ''
    });
    setAgendamentoSelecionado(null);
    setHorarioInicial(null);
  }, [dataSelecionada]);

  // 🔥 RESETAR QUANDO O MODAL FECHAR
  useEffect(() => {
    if (!showModal) {
      resetarFormData();
    }
  }, [showModal, resetarFormData]);

  // Log para debug
  useEffect(() => {
    console.log('👤 useAgendamentos - Usuário logado:', {
      nivel,
      funcionarioId: usuario?.funcionarioId,
      nome: usuario?.nome,
      isFuncionario
    });
  }, [nivel, usuario, isFuncionario]);

  // Carregar funcionários do banco
  const carregarFuncionarios = useCallback(async () => {
    try {
      const data = await funcionariosService.listar();
      console.log('📦 Todos funcionários do banco:', data.map(f => ({ id: f.id, nome: f.nome })));
      
      setFuncionarios(data);
      
      if (isFuncionario && usuario?.funcionarioId) {
        const apenasOMeu = data.filter(f => f.id === usuario.funcionarioId);
        console.log('✅ FUNCIONÁRIO FILTRADO - SÓ EU:', apenasOMeu.map(f => f.nome));
        setFuncionariosFiltrados(apenasOMeu);
      } else {
        console.log('✅ ADMIN/GERENTE - TODOS FUNCIONÁRIOS');
        setFuncionariosFiltrados(data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar funcionários:', error);
    }
  }, [isFuncionario, usuario]);

  // Carregar clientes do banco
  const carregarClientes = useCallback(async () => {
    try {
      const data = await clientesService.listar();
      
      if (isFuncionario) {
        setClientes(data.map(cliente => ({
          id: cliente.id,
          nome: cliente.nome
        })));
        console.log('✅ Clientes carregados (sem dados sensíveis):', data.length);
      } else {
        setClientes(data);
        console.log('✅ Clientes carregados (completos):', data.length);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar clientes:', error);
    }
  }, [isFuncionario]);

  // Carregar serviços do banco
  const carregarServicos = useCallback(async () => {
    try {
      const data = await servicosService.listar();
      setServicos(data);
      console.log('✅ Serviços carregados:', data.length);
    } catch (error) {
      console.error('❌ Erro ao carregar serviços:', error);
    }
  }, []);

  // Carregar agendamentos do banco
  const carregarAgendamentos = useCallback(async () => {
    try {
      setLoading(true);
      
      let data = [];
      
      if (isFuncionario && usuario?.funcionarioId) {
        console.log(`🔍 Buscando agendamentos do funcionário ID: ${usuario.funcionarioId}`);
        data = await agendamentosService.buscarPorFuncionarioPeriodo(
          usuario.funcionarioId,
          null,
          null
        );
        console.log(`✅ Encontrados ${data.length} agendamentos para este funcionário`);
      } else {
        console.log('🔍 Buscando todos os agendamentos');
        data = await agendamentosService.listar();
        console.log(`✅ Total de agendamentos: ${data.length}`);
      }
      
      const agendamentosNormalizados = data.map(ag => ({
        ...ag,
        data_hora_normalizada: ag.data_hora,
        hora_extraida: extrairHoraCorreta(ag.data_hora),
        data_extraida: extrairDataCorreta(ag.data_hora)
      }));
      
      setAgendamentos(agendamentosNormalizados);
      setAgendamentosFiltrados(agendamentosNormalizados);
    } catch (error) {
      console.error('❌ Erro ao carregar agendamentos:', error);
    } finally {
      setLoading(false);
    }
  }, [isFuncionario, usuario]);

  // 🔥 FUNÇÃO PARA REFRESH (RECARREGAR) AGENDAMENTOS
  const refreshAgendamentos = useCallback(async () => {
    console.log('🔄 RefreshAgendamentos chamado...');
    await carregarAgendamentos();
  }, [carregarAgendamentos]);

  // Carregar todos os dados iniciais
  useEffect(() => {
    const carregarDadosIniciais = async () => {
      setLoading(true);
      await Promise.all([
        carregarFuncionarios(),
        carregarClientes(),
        carregarServicos(),
        carregarAgendamentos()
      ]);
      setLoading(false);
    };
    
    carregarDadosIniciais();
  }, [carregarFuncionarios, carregarClientes, carregarServicos, carregarAgendamentos]);

  // 🔥 LISTENER PARA EVENTOS DE RECARREGAMENTO GLOBAL
  useEffect(() => {
    const handleRefresh = () => {
      console.log('🔄 Evento global de refresh recebido');
      refreshAgendamentos();
    };

    window.addEventListener('refreshAgendamentos', handleRefresh);
    window.addEventListener('agendamentoConcluido', handleRefresh);
    window.addEventListener('agendamentoAtualizado', handleRefresh);
    window.addEventListener('novoAgendamento', handleRefresh);
    window.addEventListener('agendamentoRemovido', handleRefresh);

    return () => {
      window.removeEventListener('refreshAgendamentos', handleRefresh);
      window.removeEventListener('agendamentoConcluido', handleRefresh);
      window.removeEventListener('agendamentoAtualizado', handleRefresh);
      window.removeEventListener('novoAgendamento', handleRefresh);
      window.removeEventListener('agendamentoRemovido', handleRefresh);
    };
  }, [refreshAgendamentos]);

  // FILTRO POR PROFISSIONAL E SERVIÇO
  useEffect(() => {
    if (!agendamentos.length) return;
    
    if (isFuncionario) {
      setAgendamentosFiltrados(agendamentos);
      return;
    }
    
    let filtrados = agendamentos;
    
    if (filtroProfissional !== 'todos') {
      filtrados = filtrados.filter(
        ag => ag.funcionario_id === parseInt(filtroProfissional)
      );
    }
    
    if (filtroServico !== 'todos') {
      filtrados = filtrados.filter(
        ag => ag.servico_id === parseInt(filtroServico)
      );
    }
    
    setAgendamentosFiltrados(filtrados);
  }, [agendamentos, filtroProfissional, filtroServico, isFuncionario]);

  // 🔥 FUNÇÃO PARA ABRIR NOVO AGENDAMENTO
  const abrirNovoAgendamento = (profissionalId = null, horario = null, data = null) => {
    console.log('➕ Abrindo novo agendamento', { profissionalId, horario, data });
    
    if (!podeEditar('agendamentos')) {
      alert('Você não tem permissão para criar agendamentos');
      return;
    }

    const dataParaUsar = data || format(dataSelecionada, 'yyyy-MM-dd');
    const profissionalParaUsar = profissionalId || (isFuncionario ? usuario?.funcionarioId : '');
    const horarioParaUsar = horario || '09:00';
    
    setHorarioInicial(horarioParaUsar);
    
    setAgendamentoSelecionado(null);
    setFormData({
      clienteId: '',
      profissionalId: profissionalParaUsar,
      servicoId: '',
      data: dataParaUsar,
      hora: horarioParaUsar,
      status: 'agendado',
      observacoes: ''
    });
    setShowModal(true);
  };

  // 🔥 FUNÇÃO PARA ABRIR EDIÇÃO DE AGENDAMENTO
  const abrirEditarAgendamento = (agendamento) => {
    console.log('🔍 VERIFICAÇÃO DE EDIÇÃO:');
    console.log('   Agendamento ID:', agendamento.id);
    console.log('   Data/Hora original (banco):', agendamento.data_hora);
    
    const horaCorreta = extrairHoraCorreta(agendamento.data_hora);
    const dataCorreta = extrairDataCorreta(agendamento.data_hora);
    
    console.log('   Hora extraída (UTC original):', horaCorreta);
    console.log('   Data extraída:', dataCorreta);

    if (!podeEditar('agendamentos')) {
      alert('Você não tem permissão para editar agendamentos');
      return;
    }

    if (isFuncionario) {
      if (agendamento.funcionario_id !== usuario?.funcionarioId) {
        alert('Você só pode editar seus próprios agendamentos!');
        return;
      }
    }

    setAgendamentoSelecionado(agendamento);
    setHorarioInicial(horaCorreta);
    setFormData({
      clienteId: agendamento.cliente_id,
      profissionalId: agendamento.funcionario_id,
      servicoId: agendamento.servico_id,
      data: dataCorreta,
      hora: horaCorreta,
      status: agendamento.status,
      observacoes: agendamento.observacoes || ''
    });
    setShowModal(true);
  };

  // 🔥 FUNÇÃO PARA SALVAR AGENDAMENTO - CORRIGIDA
  const salvarAgendamento = async (dadosCompletos) => {
    console.log('📝 salvarAgendamento recebeu:', dadosCompletos);
    
    let agendamentoData = dadosCompletos;
    
    if (!dadosCompletos.cliente_id || !dadosCompletos.funcionario_id || !dadosCompletos.servico_id) {
      console.log('⚠️ Dados incompletos, usando formData do hook');
      
      const cliente = clientes.find(c => c.id === parseInt(formData.clienteId));
      const profissional = funcionarios.find(f => f.id === parseInt(formData.profissionalId));
      const servico = servicos.find(s => s.id === parseInt(formData.servicoId));

      if (!cliente || !profissional || !servico) {
        alert('Dados incompletos. Selecione cliente, profissional e serviço.');
        return;
      }

      const valorOriginalServico = servico.preco;
      const valorPersonalizado = dadosCompletos?.valor_personalizado ?? null;
      const valorFinal = (valorPersonalizado !== null && valorPersonalizado > 0 && valorPersonalizado !== valorOriginalServico) 
        ? valorPersonalizado 
        : valorOriginalServico;
      
      // 🔥 CORREÇÃO: Usar a comissão do serviço DIRETAMENTE
      const percentualComissao = dadosCompletos?.comissao_personalizada ?? 
                                 (servico.comissao_percentual || profissional?.comissao_percentual || 30);
      
      console.log(`💰 Comissão calculada: ${percentualComissao}% (serviço: ${servico.comissao_percentual}%, profissional: ${profissional?.comissao_percentual}%)`);
      
      const valorComissao = valorFinal * (percentualComissao / 100);
      
      const dataHoraLocal = `${formData.data}T${formData.hora}:00`;

      agendamentoData = {
        cliente_id: parseInt(formData.clienteId),
        funcionario_id: parseInt(formData.profissionalId),
        servico_id: parseInt(formData.servicoId),
        data_hora: dataHoraLocal,
        status: formData.status,
        observacoes: formData.observacoes || '',
        valor: valorFinal,
        valor_original: valorOriginalServico,
        valor_personalizado: (valorPersonalizado !== null && valorPersonalizado > 0 && valorPersonalizado !== valorOriginalServico) 
          ? valorFinal 
          : null,
        comissao_personalizada: dadosCompletos?.comissao_personalizada ?? null,
        percentual_comissao: percentualComissao,
        valor_comissao: valorComissao,
        forma_pagamento: dadosCompletos?.forma_pagamento || null,
        bandeira_cartao: dadosCompletos?.bandeira_cartao || null,
        parcelas: dadosCompletos?.parcelas || 1,
        data_pagamento: dadosCompletos?.data_pagamento || null
      };
    }

    if (agendamentoSelecionado) {
      agendamentoData.id = agendamentoSelecionado.id;
    }

    console.log('📤 Enviando para API:', JSON.stringify(agendamentoData, null, 2));

    try {
      if (agendamentoSelecionado) {
        console.log(`✏️ Atualizando agendamento ID: ${agendamentoSelecionado.id}`);
        await agendamentosService.atualizar(agendamentoSelecionado.id, agendamentoData);
        console.log('✅ Atualizado com sucesso');
        
        const evento = new CustomEvent('agendamentoAtualizado', { 
          detail: { 
            tipo: 'edicao', 
            agendamento: agendamentoData
          }
        });
        window.dispatchEvent(evento);
        
        alert('Agendamento atualizado com sucesso!');
      } else {
        console.log('➕ Criando novo agendamento');
        const response = await agendamentosService.criar(agendamentoData);
        console.log('✅ Criado com sucesso', response);
        
        const evento = new CustomEvent('novoAgendamento', { 
          detail: { 
            venda: {
              funcionarioId: agendamentoData.funcionario_id,
              funcionario: funcionarios.find(f => f.id === agendamentoData.funcionario_id)?.nome,
              data: formData.data,
              valor: agendamentoData.valor,
              servico: servicos.find(s => s.id === agendamentoData.servico_id)?.nome,
              comissao: agendamentoData.valor_comissao
            }
          }
        });
        window.dispatchEvent(evento);
        
        alert('Agendamento criado com sucesso!');
      }
      
      await refreshAgendamentos();
      setShowModal(false);
      setHorarioInicial(null);
    } catch (error) {
      console.error('❌ Erro ao salvar agendamento:', error);
      const errorMsg = error.response?.data?.error || error.message;
      alert('Erro ao salvar agendamento: ' + errorMsg);
    }
  };

  // Função para excluir agendamento
  const excluirAgendamento = async (id) => {
    const agendamentoParaExcluir = agendamentos.find(ag => ag.id === id);
    
    if (!agendamentoParaExcluir) {
      alert('Agendamento não encontrado');
      return;
    }

    console.log('🗑️ Tentando excluir agendamento:', {
      id,
      funcionario_id: agendamentoParaExcluir.funcionario_id,
      meuFuncionarioId: usuario?.funcionarioId,
      nivel
    });

    if (!podeExcluir('agendamentos')) {
      alert('Você não tem permissão para excluir agendamentos');
      return;
    }

    if (isFuncionario && agendamentoParaExcluir.funcionario_id !== usuario?.funcionarioId) {
      alert('Você só pode excluir seus próprios agendamentos!');
      return;
    }

    if (window.confirm('Tem certeza que deseja excluir este agendamento?')) {
      try {
        await agendamentosService.excluir(id);
        await refreshAgendamentos();
        
        const evento = new CustomEvent('agendamentoRemovido', { 
          detail: { agendamento: agendamentoParaExcluir }
        });
        window.dispatchEvent(evento);
        
        setShowModal(false);
        alert('Agendamento excluído com sucesso!');
      } catch (error) {
        alert('Erro ao excluir agendamento');
        console.error(error);
      }
    }
  };

  // Funções auxiliares
  const buscarAgendamentosPorPeriodo = (mes, ano) => {
    return agendamentosFiltrados.filter(ag => {
      const dataAg = new Date(ag.data_hora);
      return dataAg.getUTCMonth() === mes && dataAg.getUTCFullYear() === ano;
    });
  };

  const calcularVendasPorFuncionario = (funcionarioId, mes, ano) => {
    const agendamentosPeriodo = buscarAgendamentosPorPeriodo(mes, ano);
    return agendamentosPeriodo
      .filter(ag => ag.funcionario_id === funcionarioId && ag.status !== 'cancelado')
      .reduce((total, ag) => total + (ag.valor || 0), 0);
  };

  const calcularComissoesPorFuncionario = (funcionarioId, mes, ano) => {
    const agendamentosPeriodo = buscarAgendamentosPorPeriodo(mes, ano);
    return agendamentosPeriodo
      .filter(ag => ag.funcionario_id === funcionarioId && ag.status !== 'cancelado')
      .reduce((total, ag) => total + (ag.valor_comissao || 0), 0);
  };

  const profissionais = funcionariosFiltrados;

  return {
    dataSelecionada,
    setDataSelecionada,
    showModal,
    setShowModal,
    agendamentoSelecionado,
    filtroProfissional,
    setFiltroProfissional,
    filtroServico,
    setFiltroServico,
    agendamentos: agendamentosFiltrados,
    todosAgendamentos: agendamentos,
    formData,
    setFormData,
    loading,
    abrirNovoAgendamento,
    abrirEditarAgendamento,
    salvarAgendamento,
    excluirAgendamento,
    buscarAgendamentosPorPeriodo,
    calcularVendasPorFuncionario,
    calcularComissoesPorFuncionario,
    profissionais,
    funcionarios: funcionariosFiltrados,
    clientes,
    servicos,
    permissoes: {
      podeVer: podeVer('agendamentos'),
      podeEditar: podeEditar('agendamentos'),
      podeExcluir: podeExcluir('agendamentos')
    },
    nivel,
    usuario,
    isFuncionario,
    horarioInicial,
    setHorarioInicial,
    refreshAgendamentos,
    carregarAgendamentos
  };
};
